import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { 
  Edit, 
  Activity, 
  Stethoscope, 
  Brain,
  Pill,
  History,
  FileText,
  AlertCircle,
  Syringe,
  ScrollText,
  Pencil
} from "lucide-react";
import { EditMedical } from "./modals/EditMedical";
import { gql, useQuery } from "@apollo/client";
import { TabHeader } from "../shared/components/TabHeader";
import { TabSpinner } from "../shared/components/TabSpinner";

const GET_ICD10_CODES = gql`
  query GetICD10Codes {
    getICD10Codes {
      icd10
      icd10Description
    }
  }
`;

interface ICD10Code {
  icd10: string;
  icd10Description: string;
}

interface MedicalData {
  bookingNumber?: string;
  mainNumber?: string;
  medicalProblems?: string;
  dsm5dx?: string;
  psychotropicMeds?: string;
  otherMeds?: string;
  currentMeds?: string;
  clinicalPresentation?: string;
  psychHistory?: string;
  substanceHistory?: string;
  icd10?: string;
  fin?: string;
}

interface MedicalStatusProps {
  odrnumber: number;
  data: {
    getParticipantByODRNumber: MedicalData;
  } | null;
  refetch: () => void;
}

const translateContent = (content: string): string => {
  if (!content) return "";
  const str1 = content.replace(/<br\/>/g, "").replace(/<p>/g, "");
  const str2 = str1
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
  const str3 = str2
    .replace(/&lt;br\/&gt;/g, "")
    .replace(/\r\n|\r|\n/g, "<br>")
    .replace(/<br\/>/g, "<br>")
    .replace(/\t/g, "&nbsp;&nbsp;&nbsp;&nbsp;")
    .replace(/ {2,}/g, (match: string) => "&nbsp;".repeat(match.length));
  return str3;
};

const FieldLabel = ({ children, icon: Icon }: { children: React.ReactNode, icon?: React.ElementType }) => (
    <div className="flex items-center gap-1.5 mb-1.5">
        {Icon && <Icon className="h-3.5 w-3.5 text-gray-400" />}
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{children}</h4>
    </div>
);

const FieldContent = ({ content, isHtml = false }: { content?: string, isHtml?: boolean }) => {
    if (!content || content.trim().length === 0) {
        return <span className="text-sm text-gray-400 italic">—</span>;
    }
    
    const displayContent = isHtml ? translateContent(content) : content;

    return (
        <div className="text-sm text-gray-900 leading-relaxed">
            {isHtml ? (
                <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: displayContent }} />
            ) : (
                displayContent
            )}
        </div>
    );
};

const ICD10Display = ({ icd10 }: { icd10?: string }) => {
  const { data, loading, error } = useQuery(GET_ICD10_CODES);
  
  if (!icd10) return <span className="text-sm text-gray-400 italic">—</span>;
  if (loading) return <div className="text-xs text-gray-500">Loading codes...</div>;
  if (error) return <div className="text-xs text-red-500">Error loading codes</div>;
  
  const icd10Codes = icd10.split(',').map(code => code.trim());
  const matchedCodes = icd10Codes.map(code => {
    const match = data?.getICD10Codes?.find(
      (item: ICD10Code) => item.icd10 === code
    );
    return match ? { code, description: match.icd10Description } : { code, description: null };
  });

  return (
    <div className="flex flex-wrap gap-2">
      {matchedCodes.map(({ code, description }, index) => (
        <Badge 
            key={index} 
            variant="secondary" 
            className="px-2.5 py-1 bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200 flex items-center gap-2 shadow-sm"
        >
          <span className="font-bold font-mono text-xs uppercase tracking-wider">{code}</span>
          {description && (
               <span className="font-normal text-xs opacity-80 border-l border-blue-200 pl-2 ml-1 line-clamp-1">{description}</span>
          )}
        </Badge>
      ))}
    </div>
  );
};

export const MedicalStatus: React.FC<MedicalStatusProps> = ({ odrnumber, data, refetch }) => {
  const [editTab, setEditTab] = useState<string | null>(null);

  if (!data) return <TabSpinner label="Loading medical information..." />;

  const medicalData = data.getParticipantByODRNumber;

  return (
    <div className="w-full space-y-6 pb-12">
      <TabHeader
        icon={Activity}
        title="Medical Profile"
        subtitle={medicalData.fin ? `FIN: ${medicalData.fin}` : "Clinical history and meds"}
        action={
          <Button 
            variant="outline" 
            className="border-gray-200 text-gray-700 hover:bg-gray-50"
            onClick={() => setEditTab("clinical")}
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit Medical
          </Button>
        }
      />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 w-full">
        
        {/* Left Column: Diagnosis & Clinical */}
        <Card className="shadow-sm overflow-hidden border-t-4 border-t-blue-500 h-fit">
            <CardHeader className="bg-gray-50/50 border-b border-gray-100 py-3 px-6 flex flex-row items-center justify-between space-y-0">
                <div className="flex items-center gap-2">
                    <Stethoscope className="h-4 w-4 text-blue-600" />
                    <CardTitle className="text-sm font-bold text-gray-700 uppercase tracking-wide">Diagnosis & Clinical</CardTitle>
                </div>
                <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 w-6 text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                    onClick={() => setEditTab("diagnosis")}
                    title="Edit Diagnosis & Clinical"
                >
                    <Pencil className="h-3.5 w-3.5" />
                </Button>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
                <div>
                    <FieldLabel icon={Activity}>ICD-10 Diagnoses</FieldLabel>
                    <ICD10Display icd10={medicalData.icd10} />
                </div>

                <Separator />

                <div>
                    <FieldLabel icon={Brain}>DSM-5 Diagnosis</FieldLabel>
                    <FieldContent content={medicalData.dsm5dx} />
                </div>

                <div>
                    <FieldLabel icon={AlertCircle}>Medical Problems</FieldLabel>
                    <FieldContent content={medicalData.medicalProblems} isHtml />
                </div>

                <div>
                    <FieldLabel icon={FileText}>Clinical Presentation</FieldLabel>
                    <div className="bg-gray-50 rounded-md p-4 border border-gray-100">
                        <FieldContent content={medicalData.clinicalPresentation} isHtml />
                    </div>
                </div>
            </CardContent>
        </Card>

        {/* Right Column: Medications & History */}
        <div className="space-y-6">
            
            {/* Medications Card */}
            <Card className="shadow-sm overflow-hidden border-t-4 border-t-purple-500">
                <CardHeader className="bg-gray-50/50 border-b border-gray-100 py-3 px-6 flex flex-row items-center justify-between space-y-0">
                    <div className="flex items-center gap-2">
                        <Pill className="h-4 w-4 text-purple-600" />
                        <CardTitle className="text-sm font-bold text-gray-700 uppercase tracking-wide">Medications</CardTitle>
                    </div>
                    <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 w-6 text-gray-400 hover:text-purple-600 hover:bg-purple-50"
                        onClick={() => setEditTab("medications")}
                        title="Edit Medications"
                    >
                        <Pencil className="h-3.5 w-3.5" />
                    </Button>
                </CardHeader>
                <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <FieldLabel icon={Brain}>Psychotropic Medications</FieldLabel>
                        <FieldContent content={medicalData.psychotropicMeds} isHtml />
                    </div>
                    <div>
                        <FieldLabel icon={Pill}>Current Medications</FieldLabel>
                        <FieldContent content={medicalData.currentMeds} />
                    </div>
                    <div className="md:col-span-2">
                        <FieldLabel icon={Syringe}>Other Medications</FieldLabel>
                        <FieldContent content={medicalData.otherMeds} />
                    </div>
                </CardContent>
            </Card>

            {/* History Card */}
            <Card className="shadow-sm overflow-hidden border-t-4 border-t-amber-500">
                <CardHeader className="bg-gray-50/50 border-b border-gray-100 py-3 px-6 flex flex-row items-center justify-between space-y-0">
                    <div className="flex items-center gap-2">
                        <History className="h-4 w-4 text-amber-600" />
                        <CardTitle className="text-sm font-bold text-gray-700 uppercase tracking-wide">History & Background</CardTitle>
                    </div>
                    <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 w-6 text-gray-400 hover:text-amber-600 hover:bg-amber-50"
                        onClick={() => setEditTab("history")}
                        title="Edit History"
                    >
                        <Pencil className="h-3.5 w-3.5" />
                    </Button>
                </CardHeader>
                <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <FieldLabel icon={ScrollText}>Psychiatric History</FieldLabel>
                        <FieldContent content={medicalData.psychHistory} isHtml />
                    </div>
                    <div>
                        <FieldLabel icon={AlertCircle}>Substance Use History</FieldLabel>
                        <FieldContent content={medicalData.substanceHistory} />
                    </div>
                </CardContent>
            </Card>

        </div>
      </div>

      {editTab && (
        <EditMedical
          refetch={refetch}
          isOpen={!!editTab}
          onClose={() => setEditTab(null)}
          programNumber={odrnumber}
          medicalData={medicalData}
          initialTab={editTab}
        />
      )}
    </div>
  );
};

export default MedicalStatus;
